<template>
  <v-expansion-panels multiple v-model="internalExpand">
    <v-expansion-panel>
      <v-expansion-panel-title>
        <div class="expansion-container">
          <div class="expansion-label">
            <v-icon color="error" v-if="!valid" class="expansion-icon">
              mdi-alert-circle
            </v-icon>
            {{ $t('PaymentDetails.title') }}
          </div>
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text eager>
        <v-form ref="paymentDetailsForm" v-model="valid" :readonly="hasNoEditRights">
          <v-select
            clearable
            item-value="value"
            item-title="method"
            v-model="paymentDetails.paymentMethod"
            :label="$t('PaymentDetails.payment_method_selection')"
            :rules="[
                    (value: any) => (value !== undefined && value !== null) || $t('Validations.required')
            ]"
            :items="paymentMethodSelection"
            :readonly="hasNoEditRights"
          />
          <v-text-field
            clearable
            type="date"
            v-model="formattedSepaDebitDate"
            :label="$t('PaymentDetails.sepa_debit_date')"
            :rules="[
                    (value: any) => !!value || !IsSepaDebitMethod(paymentDetails.paymentMethod) || $t('Validations.required'),
                    (value: any) => !value || isValidDate(value) || $t('Validations.date_invalid')
                ]"
            :readonly="hasNoEditRights"
          />
          <v-text-field
            clearable
            v-model="paymentDetails.mandateReference"
            :counter="MAX_MANDATE_REFERENCE_LENGTH"
            :label="$t('PaymentDetails.sepa_mandate_reference')"
            :rules="[
                    (value: any) => !!value
                      || !IsSepaDebitMethod(paymentDetails.paymentMethod)
                      || $t('Validations.required'),
                    (value: any) => hasSepaConditionForId(value)
                      || !IsSepaDebitMethod(paymentDetails.paymentMethod)
                      || $t('Validations.regex_error', {characters: getInvalidCharacters(value, SEPA_CHARACTER_REPLACER_REGEX_ID)}),
                    (value: any) => hasValidLengthOnSepaCondition(value, MAX_MANDATE_REFERENCE_LENGTH)
                      || !IsSepaDebitMethod(paymentDetails.paymentMethod)
                      || $t('Validations.max_length_invalid',{maxLength: MAX_MANDATE_REFERENCE_LENGTH})
                ]"
            :readonly="hasNoEditRights"
          />
          <v-text-field
            clearable
            v-model="paymentDetails.invoiceEmail"
            :counter="MAX_INVOICE_EMAIL_LENGTH"
            :label="$t('PaymentDetails.payment_method_invoice_email')"
            :rules="[
              (value: any) => !value || isValidEmail(value) || $t('Validations.format_invalid_email'),
            ]"
            :readonly="hasNoEditRights"
          />
        </v-form>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup lang="ts">

import {
  useI18n,
} from "@/composables/useI18n";

import {
  ref,
  unref,
  watch,
  computed,
} from "vue";
import type { PropType } from "vue";

import type {
  PaymentDetails,
} from "@/model/PaymentDetails";

import {
  PaymentMethod,
} from "@/Enums";

import {
  isValidDate,
} from "@/scripts/Validations/Validations";

import {
  hasSepaConditionForId,
  hasValidLengthOnSepaCondition,
  isStringAndHasValue,
  isValidEmail,
} from "@/scripts/Common/validators";

import {
  countCharacterSepaField,
  getInvalidCharacters,
} from "@/scripts/Common/helpers";

import {
  formatDate,
} from "@/scripts/Common/helpers/dateHelpers";

import {
  DateFormats,
} from "@/scripts/Common/DateFormats";

import {
  SEPA_CHARACTER_REPLACER_REGEX_ID,
} from "@/scripts/Constants";

import {
  MAX_MANDATE_REFERENCE_LENGTH,
  MAX_INVOICE_EMAIL_LENGTH
} from "@/components/children/PaymentDetails/scripts";

const {
  t,
} = useI18n();

const emits = defineEmits([
  'onValidChange',
  'onModelChange',
]);

const props = defineProps({
  paymentDetails: {
    required: true,
    type: Object as PropType<PaymentDetails>
  },
  hasNoEditRights: {
    required: true,
    type: Boolean as PropType<boolean>,
  },
  expanded: {
    required: false,
    type: Array as PropType<Array<number>>,
    default() {
      return [1]
    }
  },
});

const valid = ref<any>(true);
const paymentDetailsForm = ref();
const internalExpand = ref(props.expanded);

const formattedSepaDebitDate = computed({
  get: () => {
    if (!props.paymentDetails.sepaDebitDate) return '';
    return formatDate(props.paymentDetails.sepaDebitDate, DateFormats.ISO8601_LONG);
  },
  set: (value: string) => {
    props.paymentDetails.sepaDebitDate = isStringAndHasValue(value) ? value : undefined;
  }
});

const validate = async () => {
  paymentDetailsForm.value?.validate();
}

const IsSepaDebitMethod = (method: PaymentMethod | undefined) => {
  return method === PaymentMethod.baseDebit || method === PaymentMethod.companyDebit;
}

const paymentMethodSelection = [
  {method: t('PaymentDetails.payment_method_invoice'), value: PaymentMethod.invoice},
  {method: t('PaymentDetails.payment_method_base_debit'), value: PaymentMethod.baseDebit},
  {method: t('PaymentDetails.payment_method_company_debit'), value: PaymentMethod.companyDebit},
];

watch(valid, (val) => {
  const isOk = typeof val === 'boolean' ? val : !!val?.valid;
  emits('onValidChange', isOk);
});

watch(() => props.expanded, () => {
  internalExpand.value = props.expanded;
})

watch(() => props.paymentDetails, () => validate(), {deep: true})

</script>
